import React, { useState, useEffect } from "react";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import CRMDonut from "./CRMDonut";
import CallChart from "./CallChart";
import CallPerformanceChart from "./CallPerformanceChart";
import { ChevronDown } from "lucide-react";
import { getDecryptedUserFromCookie } from "../utils/getCookie";
import { decryptToken } from "../utils/getToken";

const CRMAnalytics = () => {
    const [userData, setUserData] = useState(null);
    const isLoggedIn = getDecryptedUserFromCookie("gta_mainstateuser");
    const url = process.env.REACT_APP_LINK;
    const token = decryptToken("accessToken") || "";
    const [range, setRange] = useState("today");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [customDates, setCustomDates] = useState([]);

    // ✅ This useEffect always runs — not conditional
    useEffect(() => {
        if (!isLoggedIn?.main_id) return;

        const fetchUserData = async () => {
            try {
                const response = await fetch(`${url}/register/single/${isLoggedIn.main_id}`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                const data = await response.json();
                setUserData(data?.users);
            } catch (error) {
                console.error("Failed to fetch user:", error);
            }
        };

        fetchUserData();
    }, [isLoggedIn?.main_id, url, token]);

    // ✅ All hooks must appear before any return
    const formatDate = (date) =>
        date.toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
        });

    useEffect(() => {
        const today = new Date();
        let from = new Date();
        let to = new Date();

        switch (range) {
            case "today":
                from = today;
                to = today;
                break;
            case "yesterday":
                from.setDate(today.getDate() - 1);
                to.setDate(today.getDate() - 1);
                break;
            case "last7days":
                from.setDate(today.getDate() - 6);
                break;
            case "last30days":
                from.setDate(today.getDate() - 29);
                break;
            case "last60days":
                from.setDate(today.getDate() - 59);
                break;
            case "last6months":
                from.setMonth(today.getMonth() - 6);
                from.setDate(from.getDate() + 1);
                break;
            case "last1year":
                from.setFullYear(today.getFullYear() - 1);
                from.setDate(from.getDate() + 1);
                break;
            case "custom":
                if (customDates.length === 2) {
                    setFromDate(formatDate(customDates[0]));
                    setToDate(formatDate(customDates[1]));
                    return;
                }
                break;
            default:
                from = today;
                to = today;
        }

        setFromDate(formatDate(from));
        setToDate(formatDate(to));
    }, [range, customDates]);

    // ✅ No early return — render conditionally inside JSX
    return (
        <div className="space-y-6 max-w-[1000px] mx-auto w-full">
            {!userData ? (
                <p className="text-gray-500 text-xs tracking-wide mb-5">
                    Fetching your detail...
                </p>
            ) : (
                <>
                    <div className="px-4 py-3 bg-white rounded-xl shadow-md">
                        <div className="mb-3">
                            <h1 className="text-xl font-semibold text-gray-900 tracking-tight">
                                {userData.full_name}
                            </h1>
                            <p className="text-sm text-gray-600">
                                Track and analyze your team’s call performance in real time.
                            </p>
                        </div>

                        {/* Select and Flatpickr inline */}
                        <div className="flex items-center gap-3">
                            <div className="relative inline-block">
                                <select
                                    value={range}
                                    onChange={(e) => setRange(e.target.value)}
                                    className="appearance-none px-3 py-1.5 pr-8 border border-gray-200 bg-white rounded-lg text-sm text-gray-700 focus:outline-none focus:border-blue-400 cursor-pointer"
                                >
                                    <option value="today">Today</option>
                                    <option value="yesterday">Yesterday</option>
                                    <option value="last7days">Last 7 Days</option>
                                    <option value="last30days">Last 30 Days</option>
                                    <option value="last60days">Last 60 Days</option>
                                    <option value="last6months">Last 6 Months</option>
                                    <option value="last1year">Last 1 Year</option>
                                    <option value="custom">Custom Date</option>
                                </select>


                                <ChevronDown
                                    size={16}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none"
                                />
                            </div>

                            {range === "custom" && (
                                <Flatpickr
                                    options={{
                                        mode: "range",
                                        dateFormat: "m/d/Y",
                                    }}
                                    onChange={(selectedDates) => {
                                        if (selectedDates.length === 2) {
                                            setCustomDates(selectedDates);
                                        }
                                    }}
                                    className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:border-blue-400"
                                    placeholder="Select date range"
                                />
                            )}
                        </div>
                    </div>

                    <CRMDonut />
                    <CallChart />
                    <CallPerformanceChart />
                </>
            )}
        </div>
    );
};

export default CRMAnalytics;
